import React, { useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, ValidationModule, AgCheckbox } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { ServerSideRowModelModule, MultiFilterModule, SetFilterModule } from 'ag-grid-enterprise';
import { Button, Container } from 'react-bootstrap';

ModuleRegistry.registerModules([
    AllCommunityModule, 
    ServerSideRowModelModule, 
    MultiFilterModule, 
    ValidationModule,
    SetFilterModule 
]);

const THRESHOLD = 70

const ReviewContext = React.createContext({
    activeEditRowId: null,
    setActiveEditRowId: () => { },
    setOriginalRowData: () => { },
    handleCancel: () => { },
    handleSave: async () => false,
    setPopupVisible: () => { },
    showOnlyFilteredTags: false
});

const ImageRenderer = (params) => {
    const path = params.value;
    const name = params.data?.image_name;
    const src = (path && (path.startsWith('http://') || path.startsWith('https://')))? path: `/api/${path}`;
    return (
        <div className="d-flex align-items-center justify-content-center h-100 py-1">
            <img src={src} alt={name} className="img-thumbnail review-thumb"
                onClick={() => window.open(src, '_blank')} />
        </div>
    );
};

const TagEditorRenderer = forwardRef((params, ref) => {
    const { activeEditRowId, setActiveEditRowId, setPopupVisible, setOriginalRowData, showOnlyFilteredTags } = React.useContext(ReviewContext);
    const tags = (typeof params.value === 'string' ? JSON.parse(params.value) : params.value) || [];
    const [newTag, setNewTag] = useState('');
    const [newConf, setNewConf] = useState(100);
    const [editIdx, setEditIdx] = useState(null);
    const [editName, setEditName] = useState('');
    const [editConf, setEditConf] = useState(0);

    useImperativeHandle(ref, () => ({
        refresh(newParams) {
            return false;
        }
    }));

    const updateTags = (updatedTags) => {
        const rowId = Number(params.data.id);
        if (activeEditRowId !== null && activeEditRowId !== rowId) {
            setPopupVisible(true);
            return;
        }
        if (activeEditRowId === null) {
            setOriginalRowData(JSON.parse(JSON.stringify(params.data)));
        }
        params.node.setDataValue('tags', updatedTags);
        setActiveEditRowId(rowId);
    };

    const toggleActive = (idx) => {
        const updated = tags.map((t, i) => i === idx ? { ...t, is_active: t.is_active === 1 ? 0 : 1 } : t);
        updateTags(updated);
    };

    const addTag = () => {
        if (!newTag.trim()) return;
        if (tags.some(t => t.tag.toLowerCase() === newTag.trim().toLowerCase())) return;
        updateTags([...tags, { tag: newTag?.trim(), confidence: newConf, is_active: 1, is_filter: 0 }]);
        setNewTag('');
        setNewConf(100);
    };

    const startEdit = (idx) => {
        setEditIdx(idx);
        setEditName(tags[idx].tag);
        setEditConf(tags[idx].confidence);
    };

    const saveEdit = () => {
        if (editIdx === null) return;
        const updated = tags.map((t, i) => i === editIdx ? { ...t, tag: editName, confidence: Math.max(0, Math.min(100, editConf)) } : t);
        updateTags(updated);
        setEditIdx(null);
    };

    const cancelEdit = () => setEditIdx(null);

    return (
        <div className="p-2" style={{ minHeight: 80 }} onClick={e => e.stopPropagation()}>
            <div className="d-flex flex-wrap gap-1 mb-2">
                {tags?.map((t, idx) => {
                    if (showOnlyFilteredTags && t.is_filter == 0) return null;
                    return (
                        <div key={idx} className={`d-inline-flex align-items-center gap-1 rounded-pill border px-2 py-1 
                            ${(t.is_filter==0) ? `${(t.is_active==1) ? 'bg-success-subtle border-success': 'bg-secondary-subtle border-secondary'}` : 'bg-warning-subtle border-warning'}`}
                            style={{fontSize:"13px", transition: 'all 0.15s' }}>

                            {editIdx === idx ? (
                                <>
                                    <input type="text" value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); } }}
                                        className="border-0 bg-transparent p-0"
                                        style={{ width: 70, fontSize: 11, outline: 'none' }}
                                        autoFocus />
                                    <input type="number" value={editConf} min={0} max={100}
                                        onChange={e => setEditConf(parseInt(e.target.value) || 0)}
                                        onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); } }}
                                        className="border-0 bg-transparent p-0"
                                        style={{ width: 32, fontSize: 10, outline: 'none', textAlign: 'right' }} />
                                    <span className="text-success" style={{ cursor: 'pointer', fontSize: 12 }} onClick={saveEdit}>✓</span>
                                    <span className="text-secondary" style={{ cursor: 'pointer', fontSize: 12 }} onClick={cancelEdit}>✗</span>
                                </>
                            ) : (
                                <>
                                    <span className="fw-medium" style={{ cursor: 'pointer' }}
                                        onClick={() => startEdit(idx)} title="Click to edit">
                                        {t.tag?.[0]?.toUpperCase() + t.tag?.slice(1)}
                                    </span>
                                    <small className="opacity-75">{t.confidence}%</small>
                                    <i
                                        className={`bi ${t.is_active ? 'bi-toggle-on' : 'bi-toggle-off'}`}
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '20px',
                                            color: t.is_active ? 'green' : '#696969'
                                        }}
                                        onClick={() => toggleActive(idx)}
                                        title={t.is_active ? 'Deactivate tag' : 'Activate tag'}/>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Add new tag row */}
            <div className="d-flex align-items-center gap-1">
                <input type="text" placeholder="new tag" value={newTag}
                    onChange={e => setNewTag(e.target.value)}
                    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    className="form-control form-control-sm border py-0 px-1"
                    style={{ fontSize: 11, width: 90, height: 22 }} />
                <input type="number" value={newConf} min={0} max={100}
                    onChange={e => setNewConf(parseInt(e.target.value) || 0)}
                    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    className="form-control form-control-sm border py-0 px-1"
                    style={{ fontSize: 11, width: 42, height: 22 }} />
                <Button className="btn-dark py-0 px-2" style={{ fontSize: 10, height: 22, borderRadius: 6 }}
                    onClick={addTag}>+</Button>
            </div>
        </div>
    );
});

//Apply/Save Button 
const ActionRenderer = (params) => {
    const { activeEditRowId, handleCancel, handleSave } = React.useContext(ReviewContext);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState('');
    const isEdited = activeEditRowId === Number(params.data.id);

    const onSaveClick = async () => {
        setLoading(true);
        setStatus('');
        const success = await handleSave();
        if (success) {
            setStatus('success');
            setTimeout(() => setStatus(''), 2000);
        } else {
            setStatus('error');
            setTimeout(() => setStatus(''), 2000);
        }
        setLoading(false);
    };

    const variant = status === 'success' ? 'btn-success' : status === 'error' ? 'btn-danger' : 'btn-dark';
    const label = loading ? null : status === 'success' ? 'Saved ✓' : status === 'error' ? 'Failed ✗' : 'Save';

    return (
        <div className="d-flex align-items-center justify-content-center gap-2 h-100">
            <Button
                className={`btn-sm px-4 py-1 ${variant}`}
                onClick={onSaveClick}
                disabled={!isEdited || loading}
                style={{
                    fontSize: '12px', minWidth: 64, borderRadius: 15,
                    transition: 'all 0.3s',
                    opacity: isEdited ? 1 : 0.80,
                    cursor: isEdited ? 'pointer' : 'not-allowed'
                }}
                title={isEdited ? 'Save changes' : 'No changes to save'}>
                {loading ? <span className="spinner-border spinner-border-sm"></span> : label}
            </Button>
            {isEdited && (
                <Button
                    variant="outline-danger"
                    className="btn-sm px-3"
                    onClick={handleCancel}
                    disabled={loading}
                    style={{ fontSize: '12px', minWidth: 64, borderRadius: 15, transition: 'all 0.3s' }}
                    title="Revert all changes">
                    Cancel
                </Button>
            )}
        </div>
    );
};

const StatusRenderer = (params) => {
    const [loading, setLoading] = useState(false);
    const isActive = params.value === 'Active';

    const handleToggle = async (e) => {
        e.stopPropagation();
        const newStatus = isActive ? 0 : 1;
        const actionText = isActive ? 'deactivate' : 'activate';
        
        if (!window.confirm(`Are you sure you want to ${actionText} this image?`)) return;
        
        setLoading(true);
        try {
            const res = await fetch(`/api/images/${params.data.id}/status?status=${newStatus}`, {
                method: 'PUT'
            });
            if (res.ok) {
                params.node.setDataValue('status', newStatus === 1 ? 'Active' : 'Inactive');
            } else {
                alert(`Failed to ${actionText} image`);
            }
        } catch (err) {
            console.error("Error updating status:", err);
            alert("Error: " + err.message);
        }
        setLoading(false);
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100">
                <span className="spinner-border spinner-border-sm text-secondary"></span>
            </div>
        );
    }

    return (
        <div className="d-flex align-items-center justify-content-center h-100">
            <div 
                onClick={handleToggle}
                className={`d-inline-flex align-items-center gap-1 ${isActive ? 'px-3':'px-2'} rounded-pill shadow-sm border`}
                style={{ 
                    cursor: 'pointer', 
                    transition: 'all 0.2s ease-in-out', 
                    fontWeight: 600,
                    backgroundColor: isActive ? "#0f581f":"#93969496",
                    color: isActive ? "#ffffff":"#000000",
                    outline: isActive ? '2px solid #0f5815' : '2px solid #929292'
                }}
                title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
                <div className={`rounded-circle ${isActive ? 'text-light' : 'text-dark'}`} style={{ width: '7px', height: '7px', backgroundColor: isActive ? '#ffffff' : '#000000' }}></div>
                {isActive ? 'Active' : 'Inactive'}
            </div>
        </div>
    );
};

// ─── Main Component ───
const ManualReview = ({ selectedDomain }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const gridRef = useRef(null);
    const [activeEditRowId, setActiveEditRowId] = useState(null);
    const [originalRowData, setOriginalRowData] = useState(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const [showOnlyFilteredTags, setShowOnlyFilteredTags] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const datasource = {
        getRows: function (params) {
            const serverApiUrl = `/api/get_images_metadata`;
            fetch(serverApiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params.request)
            })
                .then(httpResponse => httpResponse.json())
                .then(response => {
                    params.success({ rowData: response.data || response.rows || [], total: response.total || 0 });
                    setTotalCount(response.total || 0);
                    setActiveEditRowId(null);
                }).catch(error => {
                    console.error(error);
                    params.fail();
                });
        }
    }


    const defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        cellStyle: { textAlign: 'center' }
    }

    const onGridReady = (params) => {
        params.api.setGridOption('serverSideDatasource', datasource);
    };
    // When cell editing starts on a different row, block it
    const onCellEditingStarted = useCallback((event) => {
        const rowId = Number(event.data.id);
        if (activeEditRowId !== null && activeEditRowId !== rowId) {
            event.api.stopEditing(true);
            setPopupVisible(true);
        } else if (activeEditRowId === null) {
            setOriginalRowData(JSON.parse(JSON.stringify(event.data)));
        }
    }, [activeEditRowId]);

    // When a cell value changes, mark that row as the active edit
    const onCellValueChanged = useCallback((event) => {
        if (['category', 'type', 'description', 'tags'].includes(event.colDef.field)) {
            setActiveEditRowId(Number(event.data.id));
        }
    }, []);


    const handleSave = useCallback(async () => {
        const api = gridRef.current?.api;
        if (!api || activeEditRowId === null) return false;
        
        const node = api.getRowNode(String(activeEditRowId));
        if (!node) return false;

        try {
            const res = await fetch('/api/update_image_metadata', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    image_id: node.data.id,
                    description: node.data.description,
                    category: node.data.category,
                    type: node.data.type,
                    is_active_image: node.data.status === 'Active' ? 1 : 0,
                    tags: typeof node.data.tags === 'string' ? JSON.parse(node.data.tags) : node.data.tags
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setActiveEditRowId(null);
                setOriginalRowData(null);
                setPopupVisible(false);
                return true;
            }
        } catch {
            console.error('Error saving row');
        }
        alert('Failed to save. Please try again.');
        return false;
    }, [activeEditRowId]);

    const handleCancel = useCallback(() => {
        if (activeEditRowId !== null && originalRowData) {
            const node = gridRef.current?.api?.getRowNode(String(activeEditRowId));
            if (node) {
                node.setData(originalRowData);
            }
        }
        setActiveEditRowId(null);
        setOriginalRowData(null);
        setPopupVisible(false);
    }, [activeEditRowId, originalRowData]);

    const columnDefs = useMemo(() => [
        {
            headerName: 'Preview', field: 'image_path',
            cellRenderer: ImageRenderer,
            width: 90, sortable: false, filter: false, resizable: false
        },
        {
            headerName: 'Image Name', field: 'image_name',
            width: 160, sortable: true, filter: 'agTextColumnFilter'
        },
        {
            headerName: 'Category', field: 'category',
            width: 120, editable: true, sortable: true, filter: 'agTextColumnFilter',
            cellEditor: 'agTextCellEditor', valueFormatter: params => params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : ''  // Capitalize first letter
        },
        {
            headerName: 'Type', field: 'type',
            width: 120, editable: true, sortable: true, filter: 'agTextColumnFilter',
            cellEditor: 'agTextCellEditor', valueFormatter: params => params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : ''  // Capitalize first letter
        },
        {
            headerName: 'Description', field: 'description', hide: true,
            width: 260, editable: true, sortable: true, filter: 'agTextColumnFilter',
            cellEditor: 'agLargeTextCellEditor',
            cellEditorParams: { maxLength: 500, rows: 3 }
        },
        {
            headerName: 'Tags', field: 'tags',
            cellRenderer: TagEditorRenderer,
            minWidth: 350, flex: 1,
            autoHeight: true,
            sortable: false, 
            filter: 'agMultiColumnFilter',
            filterParams: {
                filters: [
                    {
                        filter: 'agTextColumnFilter',
                        display: 'accordion',
                        title: 'Filter by Tag Name'
                    },
                    {
                        filter: 'agNumberColumnFilter',
                        display: 'accordion',
                        title: 'Filter by Confidence',
                        filterOptions: ['equals', 'greaterThan', 'lessThan']
                    }
                ]
            }
        },
        {
            headerName: 'Status', field: 'status',
            cellRenderer: StatusRenderer,
            width: 100, sortable: false, filter: 'agSetColumnFilter', filterParams: {values:['Active', 'Inactive']}, resizable: false
        },
        {
            headerName: 'Update', width: 170,
            cellRenderer: ActionRenderer,
            sortable: false, filter: false, resizable: false, hide: activeEditRowId === null
        }
    ], [activeEditRowId]);

    const gridContext = useMemo(() => ({
        activeEditRowId, setActiveEditRowId, setOriginalRowData, handleCancel, handleSave, setPopupVisible, showOnlyFilteredTags
    }), [activeEditRowId, setOriginalRowData, handleCancel, handleSave, setPopupVisible, showOnlyFilteredTags]);

    return (
        <div className="container-fluid px-4 my-0 py-0 review-page-container">

            {/* Unsaved-changes popup */}
            {popupVisible && (
               <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 12, padding: '28px 32px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        minWidth: 360, maxWidth: 440, textAlign: 'center'
                    }}>
                        <div style={{ fontSize: 20, marginBottom: 8 }}></div>
                        <h5 style={{ fontWeight: 600, marginBottom: 8 }}>Unsaved Changes</h5>
                        <p style={{ color: '#555', fontSize: 14, marginBottom: 20 }}>
                            You have unsaved changes in another row.<br />
                            Save or discard them before editing a different row.
                        </p>
                        <div className="d-flex justify-content-center gap-3">
                            <Button className="btn-primary-custom btn-sm px-4"
                                onClick={handleSave}
                                style={{ borderRadius: 15, fontSize: 13 }}>
                                Save Changes
                            </Button>
                            <Button variant="outline-danger" className="btn-sm px-4"
                                onClick={handleCancel}
                                style={{ borderRadius: 15, fontSize: 13 }}>
                                Discard Changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger shadow-sm d-flex justify-content-between align-items-center">
                    <span><strong>Error:</strong> {error}</span>
                    <button className="btn btn-close" onClick={() => setError(null)}></button>
                </div>
            )}

            {loading ? (
                <div className="d-flex flex-column align-items-center justify-content-center my-5 py-5">
                    <div className="spinner-border text-dark" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-secondary mt-3">Loading catalog...</p>
                </div>
            ) : (
                <ReviewContext.Provider value={gridContext}>
                    <div className="overflow-hidden bg-white d-flex flex-column">
                        <div className="px-3 pb-2 d-flex align-items-center justify-content-end border-bottom">
                            <div className="border border-1 border-success rounded-2 px-3 py-1 me-4">
                                 <span className=" text-success fw-medium">Count: {totalCount.toLocaleString()}</span>
                            </div>
                            <div className="form-check form-switch mb-0">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    role="switch" 
                                    id="filterTagsSwitch" 
                                    checked={showOnlyFilteredTags} 
                                    onChange={(e) => setShowOnlyFilteredTags(e.target.checked)} 
                                    style={{ cursor: 'pointer' }}
                                />
                                <label className="form-check-label ms-2" htmlFor="filterTagsSwitch" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                                    Show only filtered tags
                                </label>
                            </div>
                        </div>
                        <div className="w-100 ag-theme-balham" style={{ height: '75vh' }} id="ag-grid-wrapper">
                            <AgGridReact
                                key={selectedDomain?.id || 1}
                                ref={gridRef}
                                theme="ag-theme-balham"
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                context={gridContext}
                                onCellValueChanged={onCellValueChanged}
                                onCellEditingStarted={onCellEditingStarted}
                                rowHeight={80}
                                getRowId={(params) => String(params.data.id)}
                                onGridReady={onGridReady}
                                rowHoverHighlight={true}
                                rowModelType={'serverSide'}
                                alwaysShowHorizontalScroll={false}
                                alwaysShowVerticalScroll={false}
                                popupParent={document.body}
                                suppressContextMenu={true}
                            />
                        </div>
                    </div>
                </ReviewContext.Provider>
            )}
        </div>
    );
};

export default ManualReview;
